<script lang="ts">
  import isMobile from 'is-mobile';
  import {getContext} from 'svelte';
  import type {GetContextForm} from 'Types/event';
  import {newStyleStore} from 'Store/newStyleStore';
  import type {HTMLInputAttributes} from 'svelte/elements';

  export let name: string = '';
  export let value: string = '';
  export let label: string = '';
  export let subText: string = '';
  export let helpText: string = '';
  export let type: string = '';
  export let placeholder: string = '';
  export let required: boolean = false;
  export let disabled: boolean = false;
  export let newLayout: boolean = false;
  export let isFocused: boolean = false;
  export let autocomplete: string = 'on';
  export let containerClass: string = 'form-group';
  export let id: string = '';
  export let readonly: boolean = false;
  export let hasTooltip: boolean = false;
  export let showPickerOnClick: boolean = false;
  export let applyUnifiedIssueNavStyles: boolean = false;
  export let inputmode: HTMLInputAttributes['inputmode'] = 'text';

  let input: HTMLInputElement;

  $: computedId = id || name;

  const {errors, onInput, onBlur} = getContext('form') as GetContextForm;

  const shouldShowPicker = (trigger: string): boolean => {
    return (
      showPickerOnClick &&
      'showPicker' in HTMLInputElement.prototype &&
      (trigger === 'LABEL' || (trigger === 'INPUT' && isMobile()))
    );
  };

  const handleClick = (e: Event): void => {
    const {nodeName: trigger} = e.target as HTMLElement;

    if (shouldShowPicker(trigger)) {
      input?.showPicker();
    }
  };

  const inputType = (node: HTMLInputElement): void => {
    node.type = type;
  };
</script>

<div
  class={containerClass}
  class:newLayout
  class:new-style={$newStyleStore}
  class:unified-form-style={applyUnifiedIssueNavStyles}
>
  {#if label}
    <label
      for={computedId}
      class={$$props.labelClass}
      on:click={handleClick}
      class:newLayout
      class:unified-label-style={applyUnifiedIssueNavStyles}
    >
      {label}
    </label>
  {/if}
  {#if subText}
    <p class:unified-paragraph-style={applyUnifiedIssueNavStyles}>{subText}</p>
  {/if}
  <slot name="input">
    <input
      id={computedId}
      {name}
      use:inputType
      bind:value
      bind:this={input}
      class={`form-control ${$$props.class}
        ${$newStyleStore ? 'new-input' : 'input'}`}
      class:is-invalid={$errors?.[name]?.error}
      class:is-focused={isFocused}
      class:disabled-background={disabled}
      {placeholder}
      {required}
      {disabled}
      on:input={onInput}
      on:input
      on:blur={onBlur}
      on:keyup
      on:keydown
      on:click={handleClick}
      data-testid="input-{name}"
      {autocomplete}
      {readonly}
      {inputmode}
    />
  </slot>
  <slot name="tooltip" />
  {#if !hasTooltip && $errors?.[name]?.error && $errors?.[name]?.fullErrorMessage}
    <div class="invalid-feedback" data-testid="error-message">
      {$errors?.[name]?.fullErrorMessage}
    </div>
  {/if}
  {#if helpText}
    <small>{@html helpText}</small>
  {/if}
</div>

<style>
  .newLayout {
    & .input {
      box-sizing: border-box;
      padding: 0.625rem 0.875rem;
      gap: 0.5rem;
      height: 2.75rem;
      background: #ffffff;
      border: 0.065rem solid #d0d5dd;
      box-shadow: 0 0.065rem 0.125rem rgba(16, 24, 40, 0.05);
      border-radius: 0.25rem;
      flex: none;
      align-self: stretch;
      flex-grow: 0;
    }

    & label {
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: #344054;
    }

    & .append {
      background: #ffffff;
      box-shadow: 0 0.065rem 0.125rem rgba(16, 24, 40, 0.05);
      height: 2.75rem;
      border-color: #d0d5dd !important;
      padding: 0.675rem 0.75rem !important;
    }

    & .prepared {
      color: #667085 !important;
    }
  }

  .new-style {
    margin-bottom: 1.5rem;

    & .new-input {
      display: flex;
      padding: 0.5rem var(--3, 0.75rem);
      align-items: center;
      gap: 0.5rem;
      align-self: stretch;
      border-radius: 0.25rem;
      border: 1px solid var(--Gray-300, #d0d5dd);
      background: var(--white, #fff);
      box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    }
  }

  .unified-label-style {
    color: #354764;
    font-family: Inter, sans-serif;
    font-size: 1rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.75rem;
    margin: 0;
    padding: 0;
  }

  .unified-paragraph-style {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    text-align: left;
    color: #667085;
    margin: 0;
    padding: 0;
  }

  .unified-form-style {
    padding-bottom: 1rem;
    margin-bottom: 0;

    &:last-child {
      padding-bottom: 0;
    }
  }

  .disabled-background {
    background-color: #f9fafb !important;
  }
</style>
